import React, { useState } from 'react';
import { FlexibleCRUDTable } from '@/components/common/FlexibleCRUDTable';
import { abacResourceEntity } from '@/config/entities/abacResources';
import { abacResourceService } from '@/services/abac';
import { Modal } from '@/components/ui';
import { Badge } from '@/components/ui';
import { Button } from '@/components/ui';
import { useLanguage } from '@/state/language/LanguageProvider';
import AttributeAssignmentModal from '@/components/abac/AttributeAssignmentModal';
import { toast } from 'react-hot-toast';

interface Resource {
  id: string;
  name?: string;
  title?: string;
  resource_type?: string;
  status?: string;
  custom_attributes?: Array<{
    value_id: string;
    attribute_name: string;
    value: any;
    data_type?: string;
  }>;
}

// Helper function to format attribute values based on data type
const formatAttributeValue = (value: any, dataType?: string): string => {
  if (value === null || value === undefined) {
    return 'N/A';
  }

  // If data type is provided, use it for formatting
  if (dataType) {
    switch (dataType) {
      case 'JSON':
        try {
          return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
        } catch {
          return String(value);
        }
      case 'BOOLEAN':
        return value ? 'Yes' : 'No';
      case 'DATE':
        return new Date(value).toLocaleDateString();
      case 'NUMBER':
        return Number(value).toString();
      case 'STRING':
      default:
        return String(value);
    }
  }

  // Fallback: try to detect type and format accordingly
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

  return String(value);
};

const ViewResourcesPage: React.FC = () => {
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [showResourceDetails, setShowResourceDetails] = useState(false);
  const [showAttributeAssignment, setShowAttributeAssignment] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const { t } = useLanguage();

  const handleAssignAttributes = (resource: Resource) => {
    setSelectedResource(resource);
    setShowAttributeAssignment(true);
  };

  const handleAttributeAssigned = () => {
    // Refresh the table data
    setRefreshKey(prev => prev + 1);
  };

  const handleDeleteAttributeValue = async (valueId: string) => {
    try {
      await abacResourceService.deleteAttributeValue(valueId);
      toast.success(t('abac.attributeValueDeleted'));
      // Refresh the table data
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Failed to delete attribute value:', error);
      toast.error(t('abac.failedToDeleteAttributeValue'));
    }
  };

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('abac.assignResourceAttributesAndValues')}</h1>
        <p className="text-gray-600 mt-2">
          {t('abac.assignResourceAttributesAndValuesDescription')}
        </p>
      </div>

      <FlexibleCRUDTable
        key={refreshKey}
        entity={{
          ...abacResourceEntity,
          fields: abacResourceEntity.fields.map(field => {
            if (field.key === 'custom_attributes') {
              return {
                ...field,
                render: (value: any[], _resource: any) => {
                  if (!value || value.length === 0) {
                    return 'No attributes assigned';
                  }
                  
                  const formatValue = (val: any) => {
                    if (val === null || val === undefined) return 'N/A';
                    
                    // Check if it's a date string (ISO format)
                    if (typeof val === 'string' && val.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
                      try {
                        return new Date(val).toLocaleDateString();
                      } catch {
                        return val;
                      }
                    }
                    
                    // Check if it's a boolean
                    if (typeof val === 'boolean') {
                      return val ? 'Yes' : 'No';
                    }
                    
                    return String(val);
                  };
                  
                  return (
                    <div className="space-y-1">
                      {value.map((attr: any, index: number) => (
                        <div key={index} className="flex items-center justify-between text-xs">
                          <span>
                            {attr.attribute_name}: {Array.isArray(attr.value) 
                              ? attr.value.map((v: any) => formatValue(v)).join(', ')
                              : formatValue(attr.value)
                            }
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteAttributeValue(attr.value_id);
                            }}
                            className="ml-2 text-red-600 hover:text-red-800 text-xs font-bold"
                            title="Delete this attribute value"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  );
                }
              };
            }
            return field;
          })
        }}
        service={abacResourceService}
        searchPlaceholder={t('abac.viewResources.searchPlaceholder')}
        customActions={[
          {
            label: t('abac.assignAttribute'),
            icon: () => <span>➕</span>, // Using emoji as placeholder icon
            onClick: handleAssignAttributes,
            variant: 'primary'
          }
        ]}
      />

      {/* Resource Details Modal */}
      <Modal
        isOpen={showResourceDetails}
        onClose={() => setShowResourceDetails(false)}
        title={`Resource Details: ${selectedResource?.name || selectedResource?.title || selectedResource?.id}`}
        size="lg"
      >
        {selectedResource && (
          <div className="space-y-6">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">{t('abac.basicInformation')}</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">{t('common.name')}:</span>
                  <p className="font-medium">{selectedResource.name || selectedResource.title || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-gray-500">{t('common.id')}:</span>
                  <p className="font-medium">{selectedResource.id}</p>
                </div>
                {selectedResource.resource_type && (
                  <div>
                    <span className="text-gray-500">{t('abac.resourceType')}:</span>
                    <p className="font-medium">{selectedResource.resource_type}</p>
                  </div>
                )}
                {selectedResource.status && (
                  <div>
                    <span className="text-gray-500">{t('common.status')}:</span>
                    <p className="font-medium">{selectedResource.status}</p>
                  </div>
                )}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-700">{t('abac.assignedAttributes')}</h4>
                <Button
                  size="sm"
                  onClick={() => {
                    setShowResourceDetails(false);
                    setShowAttributeAssignment(true);
                  }}
                >
                  {t('abac.assignAttribute')}
                </Button>
              </div>
              {selectedResource.custom_attributes && selectedResource.custom_attributes.length > 0 ? (
                <div className="space-y-3">
                  {selectedResource.custom_attributes.map((attr: any, index: number) => (
                    <div key={index} className="border rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">{attr.attribute_name}</span>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => handleDeleteAttributeValue(attr.value_id)}
                          className="text-xs"
                        >
                          ✕
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {attr.data_type === 'JSON' ? (
                          <div className="w-full">
                            <pre className="text-xs bg-gray-50 p-2 rounded border overflow-x-auto whitespace-pre-wrap">
                              {formatAttributeValue(attr.value, attr.data_type)}
                            </pre>
                          </div>
                        ) : (
                          <Badge variant="outline" className="text-xs">
                            {formatAttributeValue(attr.value, attr.data_type)}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">{t('abac.noAttributesAssignedToResource')}</p>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Attribute Assignment Modal */}
      {selectedResource && (
        <AttributeAssignmentModal
          isOpen={showAttributeAssignment}
          onClose={() => setShowAttributeAssignment(false)}
          entityType="RESOURCE"
          entity={selectedResource}
          onAttributeAssigned={handleAttributeAssigned}
        />
      )}
    </div>
  );
};

export default ViewResourcesPage;
